import { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import { useRefresh } from '../context/RefreshContext';

export function useTeamStats(startDate, endDate) {
  const [hsRecords, setHsRecords] = useState([]);
  const [onoStats, setOnoStats] = useState({ weeklyTotal: 0, ytdTotal: 0, breakdown: [] });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { refreshCount } = useRefresh();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    Promise.all([
      apiClient.get('/teams/hs', { params }).catch(() => ({ data: { records: [] } })),
      apiClient.get('/teams/ono/stats', { params }).catch(() => ({ data: { weeklyTotal: 0, ytdTotal: 0, breakdown: [] } })),
      apiClient.get('/teams/employees').catch(() => ({ data: { employees: [] } })),
    ]).then(([hsRes, onoRes, empRes]) => {
      if (cancelled) return;
      setHsRecords(hsRes.data.records || []);
      setOnoStats(onoRes.data);
      setEmployees(empRes.data.employees || []);
    }).catch(err => {
      if (!cancelled) setError(err.response?.data?.error || 'Failed to load team data');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [refreshCount, startDate, endDate]);

  // Compute H&S compliance
  const totalHs = hsRecords.length;
  const completedHs = hsRecords.filter(r => r.status === 'completed').length;
  const pendingHs = hsRecords.filter(r => r.status === 'pending').length;
  const overdueHs = hsRecords.filter(r => {
    if (r.status === 'completed') return false;
    if (!r.due_date) return false;
    return new Date(r.due_date) < new Date();
  }).length;
  const hsCompliancePct = totalHs > 0 ? Math.round((completedHs / totalHs) * 100) : 100;

  // 1-on-1 stats
  const totalEmployees = employees.filter(e => e.active !== 0).length;
  const onoCompleted = onoStats.weeklyTotal || 0;
  const onoBreakdown = onoStats.breakdown || [];
  const employeesWithOno = onoBreakdown.filter(b => b.weekly > 0).length;
  const onoCompletionPct = totalEmployees > 0 ? Math.round((employeesWithOno / totalEmployees) * 100) : 0;

  return {
    loading,
    error,
    hs: {
      total: totalHs,
      completed: completedHs,
      pending: pendingHs,
      overdue: overdueHs,
      compliancePct: hsCompliancePct,
    },
    ono: {
      weeklyCompleted: onoCompleted,
      ytdCompleted: onoStats.ytdTotal || 0,
      totalEmployees,
      employeesWithOno,
      completionPct: onoCompletionPct,
      breakdown: onoBreakdown,
    },
  };
}
